import React from "react";
import "./AngledBackgroundSection.scss";

type ShapeType = "none" | "angle" | "wave";
type AngleDir = "left" | "right";

type ShapeConfig = {
    type?: ShapeType;
    height?: number;                 // px
    cutoutColor?: string;            // the "page background" color (the triangle/wave color)
    angleDirection?: AngleDir;       // for type="angle"
    waveFlipX?: boolean;             // for type="wave" (mirror horizontally)
};

type LabelMode = "standard" | "anchored";

interface Props {
    children: React.ReactNode;

    /** Optional big label on the top shape */
    labelText?: string;
    labelMode?: LabelMode;

    /** Main fill color of the section */
    backgroundColor: string;

    /** Section wrapper background (page color behind the cutouts) */
    pageColor?: string;

    /** Padding inside the content area */
    contentPaddingY?: number; // px
    contentPaddingX?: number; // px

    /** Top and bottom shape configs */
    top?: ShapeConfig;
    bottom?: ShapeConfig;

    /** Label customization */
    labelAnchor?: "shape" | "content";
    labelOnShapeY?: number;      // ratio of top height, e.g. 0.7
    labelOffsetY?: number; 
    labelLeft?: string;   // css value like "5vw" or "24px"
    labelBottom?: string; // css value like "0px" or "12px"
    labelSize?: string;   // css value like "10vh" or "64px"
    labelRotateDeg?: number;
    labelFontFamily?: string;

    /** Anchored label controls */
    labelFillColor?: string;   // color of the label text block
    labelPadding?: string;     // CSS padding for label block (e.g. "8px 16px")
    labelMaxWidth?: string;    // e.g. "90vw" or "1200px"
    labelOpacity?: number;     // e.g. 0.95

    className?: string;
}

const Wave: React.FC<{
    height: number;
    fill: string;
    position: "top" | "bottom";
    flipX?: boolean;
}> = ({ height, fill, position, flipX }) => {
    // Two distinct shapes so "top" and "bottom" never feel inverted.
    const pathTop =
        "M0,60 C15,90 35,90 50,60 C65,30 85,30 100,60 L100,0 L0,0 Z";
    const pathBottom =
        "M0,40 C15,10 35,10 50,40 C65,70 85,70 100,40 L100,100 L0,100 Z";

    const transform = flipX ? "scale(-1,1) translate(-100,0)" : undefined;

    return (
        <svg
            className="habts-wave"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ height }}
            aria-hidden="true"
            focusable="false"
        >
            <g transform={transform}>
                <path d={position === "top" ? pathTop : pathBottom} fill={fill} />
            </g>
        </svg>
    );
};

export const AngledBackgroundSection: React.FC<Props> = ({
    children,
    labelText = "",
    backgroundColor,
    pageColor = "#ffffff",
    contentPaddingY = 24,
    contentPaddingX = 0,
    top = { type: "angle", height: 80, angleDirection: "left" },
    bottom = { type: "angle", height: 80, angleDirection: "right" },
    labelLeft = "5vw",
    labelBottom = "0px",
    labelSize = "10vh",
    labelRotateDeg = -6,
    labelFontFamily = "Oswald, sans-serif",
    className = "",
}) => {
    const topType = top?.type ?? "none";
    const bottomType = bottom?.type ?? "none";
    const topHeight = top?.height ?? 80;
    const bottomHeight = bottom?.height ?? 80;

    const cutoutTop = top?.cutoutColor ?? pageColor;
    const cutoutBottom = bottom?.cutoutColor ?? pageColor;

    return (
        <section
            className={`habts ${className}`}
            style={
                {
                    "--habts-bg": backgroundColor,
                    "--habts-page": pageColor,
                    "--habts-top-h": `${topHeight}px`,
                    "--habts-bottom-h": `${bottomHeight}px`,
                    "--habts-pad-y": `${contentPaddingY}px`,
                    "--habts-pad-x": `${contentPaddingX}px`,
                    "--habts-label-left": labelLeft,
                    "--habts-label-bottom": labelBottom,
                    "--habts-label-size": labelSize,
                    "--habts-label-rot": `${labelRotateDeg}deg`,
                    "--habts-label-font": labelFontFamily,
                } as React.CSSProperties
            }
        >
            {/* TOP SHAPE */}
            {topType !== "none" && (
                <div
                    className={[
                        "habts-shape",
                        "habts-shape--top",
                        topType === "angle" ? "habts-shape--angle" : "",
                    ].join(" ")}
                    data-angle-dir={top?.angleDirection ?? "left"}
                    style={{ height: topHeight, backgroundColor: backgroundColor }}
                >
                    {topType === "wave" && (
                        <Wave
                            height={topHeight}
                            fill={cutoutTop}
                            position="top"
                            flipX={top?.waveFlipX}
                        />
                    )}

                    {!!labelText && (
                        <div className="habts-label" aria-hidden="true">
                            {labelText}
                        </div>
                    )}
                </div>
            )}

            {/* CONTENT */}
            <div className="habts-content">{children}</div>

            {/* BOTTOM SHAPE */}
            {bottomType !== "none" && (
                <div
                    className={[
                        "habts-shape",
                        "habts-shape--bottom",
                        bottomType === "angle" ? "habts-shape--angle" : "",
                    ].join(" ")}
                    data-angle-dir={bottom?.angleDirection ?? "right"}
                    style={{ height: bottomHeight, backgroundColor: backgroundColor }}
                >
                    {bottomType === "wave" && (
                        <Wave
                            height={bottomHeight}
                            fill={cutoutBottom}
                            position="bottom"
                            flipX={bottom?.waveFlipX}
                        />
                    )}
                </div>
            )}
        </section>
    );
};